/**
 * EDITORIAL_STORY safe formatting. Approved paragraph strings remain the
 * source of truth. No HTML, arbitrary CSS, external Markdown parser or eval.
 */
export function storyParagraphKind(value) {
  const text = typeof value === "string" ? value : "";
  if (/^##[ \t]+/.test(text)) return {kind:"heading",body:text.replace(/^##[ \t]+/,"")};
  if (/^>[ \t]+/.test(text)) return {kind:"quote",body:text.replace(/^>[ \t]+/,"")};
  return {kind:"paragraph",body:text};
}

const MARKS = /\*\*\*([^\n]+?)\*\*\*|\*\*([^\n]+?)\*\*|\*([^*\n]+?)\*/g;

export function tokenizeStoryInline(value) {
  const text = typeof value === "string" ? value : "";
  const result = [];
  const push = (text, mark = "text") => {
    if (!text) return;
    const last = result[result.length - 1];
    if (last && last.mark === mark) last.text += text;
    else result.push({text,mark});
  };
  const parse = (source, inherited = "text", depth = 0) => {
    if (depth > 2) { push(source,inherited); return; }
    let offset = 0;
    for (const match of source.matchAll(MARKS)) {
      if (match.index > offset) push(source.slice(offset, match.index), inherited);
      const mark = match[1] !== undefined ? "bolditalic" :
        match[2] !== undefined ? "bold" : "italic";
      const raw = match[1] ?? match[2] ?? match[3];
      if (!raw.trim()) { push(match[0],inherited); offset=match.index+match[0].length; continue; }
      const left=raw.match(/^\s*/)[0], right=raw.match(/\s*$/)[0];
      const center=raw.slice(left.length,raw.length-right.length);
      push(left,inherited);
      const combined = inherited==="text" ? mark :
        inherited==="bolditalic" || inherited!==mark ? "bolditalic" : mark;
      if (mark==="bold" && center.includes("*") && depth<2)
        parse(center,combined,depth+1);
      else push(center,combined);
      push(right,inherited);
      offset=match.index+match[0].length;
    }
    if (offset < source.length) push(source.slice(offset), inherited);
  };
  parse(text);
  return result;
}

/** Mutate just the selected paragraph; repeated formatting toggles off.
 * Selection inside an already marked span removes its enclosing marks.
 */
export function applyStoryParagraphFormat(value,kind,from=0,to=0){
  const text=typeof value==="string"?value:"";
  if(kind==="heading"||kind==="quote"){
    const parsed=storyParagraphKind(text);
    return parsed.kind===kind?parsed.body:(kind==="heading"?"## ":"> ")+parsed.body;
  }
  if(kind!=="bold" && kind!=="italic") return text;
  const start=Math.max(0,Math.min(text.length,Number.isInteger(from)?from:0));
  const end=Math.max(start,Math.min(text.length,Number.isInteger(to)?to:start));
  if(start===end) return text;
  const marker=kind==="bold" ? "**":"*";
  const selected=text.slice(start,end);
  // A selection containing the formatting characters themselves.
  if(selected.startsWith(marker) && selected.endsWith(marker) && selected.length>marker.length*2)
    return text.slice(0,start)+selected.slice(marker.length,-marker.length)+text.slice(end);
  // Selecting the visible words *between* existing markers must toggle,
  // not generate nested strings like *****words*****.
  if(text.slice(0,start).endsWith(marker) && text.slice(end).startsWith(marker))
    return text.slice(0,start-marker.length)+selected+text.slice(end+marker.length);
  return text.slice(0,start)+marker+selected+marker+text.slice(end);
}
